import React, { useState, useEffect } from 'react';
import { PropTypes } from 'prop-types';
import isEqual from 'lodash/isEqual';
import { IoClose } from 'react-icons/io5';
import DateSelectorV2 from '../FormInputs/DateSelectorV2/DateSelectorV2';
import TextInput from '../FormInputs/TextInput/TextInput';
import tailwindTheme from '../../../../../config/tailwind.config';
import Modal from './Modal';
import { useUnsavedChangesWarning, useCloseDialogWithUnsavedChanges } from '../../hooks';
import Button from '../Button/Button';

const NewSeasonModal = ({
  isShowing, handleClose = () => {}, onCreate, validationErrors = {}, setValidationErrors = () => {},
}) => {
  const initialSeason = {
    id: null,
    name: '',
    start_date: '',
    end_date: '',
  };
  const [seasonData, setSeasonData] = useState(initialSeason);
  const [isUnsavedChanges, setIsUnsavedChanges] = useState(false);

  const hasUnsavedChanges = !isEqual(initialSeason, seasonData);

  useEffect(() => {
    setIsUnsavedChanges(hasUnsavedChanges);
  }, [hasUnsavedChanges, seasonData]); // seasonData - track changes to seasonData for unsaved changes if isUnsavedChanges was set to false on previous changes

  // browser warning for unsaved changes
  useUnsavedChangesWarning(isUnsavedChanges);

  const handleCloseDialog = useCloseDialogWithUnsavedChanges({
    isUnsavedChanges: hasUnsavedChanges,
    setIsUnsavedChanges,
    redirectCallback: handleClose,
  });

  // Handle date changes and format for backend
  const handleDateChange = (name, date) => {
    if (date) {
      const formattedDate = new Date(date).toISOString().split('T')[0];
      setSeasonData((prev) => ({ ...prev, [name]: formattedDate }));
      setValidationErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };
  const dateIconStyles = {
    position: 'absolute',
    top: '1.7rem',
    right: '0.1rem',
    pointerEvents: 'none',
    zIndex: 1,
  };
  const errorMessageFontClasses = 'text-vamos-red font-normal text-sm'; // no z-20 to avoid overlapping with datepicker
  return (
    <Modal isShowing={isShowing} onHide={handleCloseDialog} className="w-2/5 min-w-107 h-2/5 min-h-100 md:w-3/5 lg:w-2/5">
      <div
        className="min-h-2/5 bg-vamos-true-white rounded-md shadow-vamos-drop-shadow min-h-125 lg:min-h-100"
        onClick={e => e.stopPropagation()}
        role="button"
        tabIndex="0"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.stopPropagation();
          }
        }}
      >
        <div className="flex flex-col">
          <div className="flex items-center p-7 justify-between border-b border-vamos-blue-gray">
            <h2 className="text-2xl font-bold">Create a Season</h2>
            <button type="button" onClick={handleCloseDialog} className="focus:outline-none"><IoClose size={24} color={tailwindTheme.theme.extend.colors['vamos-black']} /></button>
          </div>
          <div className="h-full px-7 pt-8 space-y-6 text-lg">
            <div className="sm:w-3/5 lg:w-3/6">
              <TextInput
                name="name"
                isRequired
                label="Name"
                value={seasonData.name}
                setValue={(v) => {
                  setSeasonData((prev) => ({ ...prev, name: v }));
                  setValidationErrors((prev) => ({ ...prev, name: '' }));
                }}
                asteriskWidth="w-2"
                validationError={validationErrors.name && validationErrors.name}
                errorMessageClass="absolute"
                customWidth="w-full"
                inputTextPadding="px-2 py-1.5"
                isValid={!validationErrors.name}
                errorMessageFontClasses={errorMessageFontClasses}
              />
            </div>
            <div className="flex flex-col lg:flex-row lg:space-x- w-full h-full  lg:space-x-8 gap-y-6">
              <div className="flex h-full flex-col min-w-48 sm:w-3/5 w-full md:h-auto">
                <DateSelectorV2
                  name="start_date"
                  label="Start date"
                  value={seasonData.start_date}
                  setValue={(value) => handleDateChange('start_date', value)}
                  isRequired
                  dateIconStyles={dateIconStyles}
                  asteriskWidth="w-2"
                  dateFormat="MMMM d, yyyy"
                  validationError={validationErrors.start_date && validationErrors.start_date}
                  errorMessageClass="absolute"
                  customWidth="w-full"
                  inputTextPadding="px-2 py-1.5"
                  isValid={!validationErrors.start_date}
                  errorMessageFontClasses={errorMessageFontClasses}
                />
              </div>
              <div className="flex h-full flex-col min-w-48 sm:w-3/5 w-full md:h-auto">
                <DateSelectorV2
                  name="end_date"
                  label="End date"
                  value={seasonData.end_date}
                  setValue={(value) => handleDateChange('end_date', value)}
                  isRequired
                  dateIconStyles={dateIconStyles}
                  asteriskWidth="w-2"
                  dateFormat="MMMM d, yyyy"
                  validationError={validationErrors.end_date && validationErrors.end_date}
                  errorMessageClass="absolute"
                  customWidth="w-full"
                  inputTextPadding="px-2 py-1.5"
                  isValid={!validationErrors.end_date}
                  errorMessageFontClasses={errorMessageFontClasses}

                />
              </div>
            </div>
          </div>
          <div className="flex h-full flex-col items-end pr-6 pb-6 justify-end pt-8">
            <Button
              label="Create"
              onClick={() => {
                onCreate(seasonData);
              }}
              size="md"
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

NewSeasonModal.propTypes = {
  isShowing: PropTypes.bool.isRequired,
  handleClose: PropTypes.func,
  onCreate: PropTypes.func.isRequired,
  validationErrors: PropTypes.object,
  setValidationErrors: PropTypes.func,
};

export default NewSeasonModal;
